"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from "react";
import CommonDropdown from "@/components/utils/select/dropdown/CommonDropdown";
import type { CommonSelectItem } from "@/components/utils/select/CommonSelect";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import { WaveLeaderboardLoading } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoading";
import { WaveLeaderboardLoadingBar } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoadingBar";
import { ApiPageSortDirection } from "@/generated/models/ApiPageSortDirection";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import useLocalPreference from "@/hooks/useLocalPreference";
import { useWavePollDrops } from "@/hooks/useWavePollDrops";
import type { WavePollsSort as WavePollsApiSort, WavePollsState } from "@/services/api/wave-drops-v2-api";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import { useLayout } from "./layout/LayoutContext";
interface MyStreamWavePollsProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
type WavePollsStateFilter = "ALL" | "OPEN" | "CLOSED";
type WavePollsSortOption = "NEWEST" | "OLDEST" | "CLOSING_SOON" | "CLOSING_LATEST";
interface WavePollsStateFilterItem {
  readonly label: string;
  readonly value: WavePollsStateFilter;
  readonly apiState?: WavePollsState | undefined;
  readonly emptyLabel: string;
}
interface WavePollsSortItem extends CommonSelectItem<WavePollsSortOption> {
  readonly sortDirection: ApiPageSortDirection;
  readonly sort: WavePollsApiSort;
}
const DEFAULT_STATE_FILTER_ITEM: WavePollsStateFilterItem = {
  label: "All",
  value: "ALL",
  emptyLabel: "No polls to show."
};
const STATE_FILTER_ITEMS: readonly WavePollsStateFilterItem[] = [DEFAULT_STATE_FILTER_ITEM, {
  label: "Open",
  value: "OPEN",
  apiState: "OPEN",
  emptyLabel: "No open polls."
}, {
  label: "Closed",
  value: "CLOSED",
  apiState: "CLOSED",
  emptyLabel: "No closed polls."
}];
const DEFAULT_SORT_ITEM: WavePollsSortItem = {
  key: "newest",
  label: "Newest",
  value: "NEWEST",
  sortDirection: ApiPageSortDirection.Desc,
  sort: "created_at"
};
const SORT_ITEMS: readonly WavePollsSortItem[] = [DEFAULT_SORT_ITEM, {
  key: "oldest",
  label: "Oldest",
  value: "OLDEST",
  sortDirection: ApiPageSortDirection.Asc,
  sort: "created_at"
}, {
  key: "closing-soon",
  label: "Closing soon",
  value: "CLOSING_SOON",
  sortDirection: ApiPageSortDirection.Asc,
  sort: "closing_time"
}, {
  key: "closing-latest",
  label: "Closing latest",
  value: "CLOSING_LATEST",
  sortDirection: ApiPageSortDirection.Desc,
  sort: "closing_time"
}];
const hasOptionValue = <T extends string,>(items: readonly {
  readonly value: T;
}[], value: unknown): value is T => typeof value === "string" && items.some(item => item.value === value);
const isWavePollsStateFilter = (value: unknown): value is WavePollsStateFilter => hasOptionValue(STATE_FILTER_ITEMS, value);
const isWavePollsSort = (value: unknown): value is WavePollsSortOption => hasOptionValue(SORT_ITEMS, value);
const getStateFilterItem = (value: WavePollsStateFilter): WavePollsStateFilterItem => STATE_FILTER_ITEMS.find(item => item.value === value) ?? DEFAULT_STATE_FILTER_ITEM;
const getSortItem = (value: WavePollsSortOption): WavePollsSortItem => SORT_ITEMS.find(item => item.value === value) ?? DEFAULT_SORT_ITEM;
const getStateFilterClassName = (active: boolean): string => {
  const baseClass = "tw-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-leading-5 tw-transition-all tw-duration-300 tw-ease-out";
  if (active) {
    return `${baseClass} tw-bg-iron-800 tw-text-iron-100`;
  }
  return `${baseClass} tw-bg-iron-950 tw-text-iron-500 desktop-hover:hover:tw-bg-iron-900 desktop-hover:hover:tw-text-iron-100`;
};
const noop = () => {};
const MyStreamWavePolls: React.FC<MyStreamWavePollsProps> = t0 => {
  const $ = _c(56);
  const {
    wave,
    onDropClick
  } = t0;
  const {
    leaderboardViewStyle
  } = useLayout();
  const [stateFilter, setStateFilter] = useLocalPreference(`wavePollState_${wave.id}`, "ALL", isWavePollsStateFilter);
  const [sort, setSort] = useLocalPreference(`wavePollSort_${wave.id}`, "NEWEST", isWavePollsSort);
  let t1;
  if ($[0] !== wave) {
    t1 = {
      wave
    };
    $[0] = wave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    winningThreshold,
    winningThresholdMinDurationMs
  } = useApprovalWaveStatus(t1);
  let t2;
  if ($[2] !== stateFilter) {
    t2 = getStateFilterItem(stateFilter);
    $[2] = stateFilter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const stateFilterItem = t2;
  let t3;
  if ($[4] !== sort) {
    t3 = getSortItem(sort);
    $[4] = sort;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const sortItem = t3;
  let t4;
  if ($[6] !== sortItem.sort || $[7] !== sortItem.sortDirection || $[8] !== stateFilterItem.apiState || $[9] !== wave.id) {
    t4 = {
      waveId: wave.id,
      state: stateFilterItem.apiState,
      sortDirection: sortItem.sortDirection,
      sort: sortItem.sort
    };
    $[6] = sortItem.sort;
    $[7] = sortItem.sortDirection;
    $[8] = stateFilterItem.apiState;
    $[9] = wave.id;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useWavePollDrops(t4);
  let t5;
  if ($[11] !== refetch) {
    t5 = () => {
      refetch().catch(_temp);
    };
    $[11] = refetch;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleRetry = t5;
  let t6;
  if ($[13] !== fetchNextPage || $[14] !== hasNextPage || $[15] !== isFetching || $[16] !== isFetchingNextPage) {
    t6 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetching || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(_temp2);
    };
    $[13] = fetchNextPage;
    $[14] = hasNextPage;
    $[15] = isFetching;
    $[16] = isFetchingNextPage;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleIntersection = t6;
  const intersectionElementRef = useIntersectionObserver(handleIntersection);
  let content;
  if (isError && drops.length === 0) {
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <p className="tw-mb-0 tw-text-sm tw-text-iron-300">Unable to load polls.</p>;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== handleRetry) {
      t8 = <div className="tw-mt-10 tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center">{t7}<button type="button" onClick={handleRetry} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-500 tw-bg-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-iron-100 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-iron-300 desktop-hover:hover:tw-bg-iron-800">Try again</button></div>;
      $[19] = handleRetry;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    content = t8;
  } else {
    if (isFetching && drops.length === 0) {
      let t7;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <WaveLeaderboardLoading />;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      content = t7;
    } else {
      if (drops.length === 0) {
        let t7;
        if ($[22] !== stateFilterItem.emptyLabel) {
          t7 = <div className="tw-mt-10"><p className="tw-text-center tw-text-sm tw-text-iron-500">{stateFilterItem.emptyLabel}</p></div>;
          $[22] = stateFilterItem.emptyLabel;
          $[23] = t7;
        } else {
          t7 = $[23];
        }
        content = t7;
      } else {
        let t7;
        if ($[24] !== drops || $[25] !== onDropClick || $[26] !== winningThreshold || $[27] !== winningThresholdMinDurationMs) {
          let t8;
          if ($[29] !== onDropClick || $[30] !== winningThreshold || $[31] !== winningThresholdMinDurationMs) {
            t8 = drop => <Drop key={drop.stableKey} drop={drop} previousDrop={null} nextDrop={null} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} dropViewDropId={null} onReply={noop} onReplyClick={noop} onQuoteClick={noop} onDropContentClick={onDropClick} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} />;
            $[29] = onDropClick;
            $[30] = winningThreshold;
            $[31] = winningThresholdMinDurationMs;
            $[32] = t8;
          } else {
            t8 = $[32];
          }
          t7 = drops.map(t8);
          $[24] = drops;
          $[25] = onDropClick;
          $[26] = winningThreshold;
          $[27] = winningThresholdMinDurationMs;
          $[28] = t7;
        } else {
          t7 = $[28];
        }
        let t8;
        if ($[33] !== isFetchingNextPage) {
          t8 = isFetchingNextPage && <WaveLeaderboardLoadingBar />;
          $[33] = isFetchingNextPage;
          $[34] = t8;
        } else {
          t8 = $[34];
        }
        let t9;
        if ($[35] !== intersectionElementRef) {
          t9 = <div ref={intersectionElementRef} />;
          $[35] = intersectionElementRef;
          $[36] = t9;
        } else {
          t9 = $[36];
        }
        let t10;
        if ($[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
          t10 = <div className="tw-mt-4 tw-space-y-2">{t7}{t8}{t9}</div>;
          $[37] = t7;
          $[38] = t8;
          $[39] = t9;
          $[40] = t10;
        } else {
          t10 = $[40];
        }
        content = t10;
      }
    }
  }
  let t7;
  if ($[41] !== setStateFilter || $[42] !== stateFilter) {
    t7 = STATE_FILTER_ITEMS.map(item => <button key={item.value} type="button" role="tab" aria-label={item.label} aria-selected={stateFilter === item.value} tabIndex={stateFilter === item.value ? 0 : -1} className={getStateFilterClassName(stateFilter === item.value)} onClick={() => setStateFilter(item.value)}>{item.label}</button>);
    $[41] = setStateFilter;
    $[42] = stateFilter;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  let t8;
  if ($[44] !== t7) {
    t8 = <div role="tablist" aria-label="Poll state" className="tw-flex tw-items-center tw-gap-x-1 tw-rounded-lg tw-bg-iron-950 tw-p-1 tw-ring-1 tw-ring-inset tw-ring-iron-800">{t7}</div>;
    $[44] = t7;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  let t9;
  if ($[46] !== setSort || $[47] !== sort) {
    t9 = <div className="tw-w-44 tw-min-w-0"><CommonDropdown items={SORT_ITEMS} activeItem={sort} filterLabel="Sort" setSelected={setSort} size="sm" showFilterLabel={true} /></div>;
    $[46] = setSort;
    $[47] = sort;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== t8 || $[50] !== t9) {
    t10 = <div className="tw-sticky tw-top-0 tw-z-30 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-bg-black tw-py-4">{t8}{t9}</div>;
    $[49] = t8;
    $[50] = t9;
    $[51] = t10;
  } else {
    t10 = $[51];
  }
  let t11;
  if ($[52] !== content || $[53] !== leaderboardViewStyle || $[54] !== t10) {
    t11 = <div className="tw-space-y-4 tw-overflow-y-auto tw-px-2 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-px-4 lg:tw-space-y-6" style={leaderboardViewStyle}>{t10}{content}</div>;
    $[52] = content;
    $[53] = leaderboardViewStyle;
    $[54] = t10;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  return t11;
};
export default MyStreamWavePolls;
function _temp() {}
function _temp2() {}